import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSpeaker } from "@/lib/voice";
import { serviceMeta } from "@/lib/ui";
import ReadAloud from "@/components/ReadAloud";
import FamilyNote from "@/components/FamilyNote";
import { Check, X, Lightbulb } from "lucide-react";

export default function FamilySuggestions({ requests, onChanged }) {
  const drafts = requests.filter((r) => r.status === "draft");
  const [busy, setBusy] = useState("");
  const { speak } = useSpeaker();
  if (drafts.length === 0) return null;

  const decide = async (d, approve) => {
    setBusy(d.id);
    try {
      await api.post(`/requests/${d.id}/draft-decision`, { approve });
      const msg = approve ? "Booked. Helpers nearby can now see it." : "Okay, I've set that aside.";
      toast.success(msg); speak(msg); onChanged?.();
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setBusy(""); }
  };

  return (
    <section data-testid="family-suggestions" className="mb-8 space-y-4">
      {drafts.map((d) => {
        const M = serviceMeta(d.service_type).icon;
        const text = `${d.drafted_by} suggests: ${d.title}, on ${(d.scheduled_at || d.scheduled_date).replace("T", " at ")}${d.location ? `, at ${d.location}` : ""}. Shall I book it?`;
        return (
          <div key={d.id} data-testid={`suggestion-${d.id}`} className="bg-[#FFF8E6] border-2 border-[#F3D98B] rounded-3xl p-6">
            <div className="flex items-center gap-2 text-[#A9741A] font-semibold tracking-wide"><Lightbulb className="w-5 h-5" /> {d.drafted_by.toUpperCase()} SUGGESTS</div>
            <div className="flex items-start gap-4 mt-3">
              <span className="w-14 h-14 rounded-xl bg-white flex items-center justify-center shrink-0"><M className="w-7 h-7 text-[#D96B43]" /></span>
              <div className="flex-1">
                <h3 className="text-2xl font-semibold text-slate-900">{d.title}</h3>
                <p className="text-lg text-slate-700">{(d.scheduled_at || d.scheduled_date).replace("T", " at ")}{d.location ? ` · ${d.location}` : ""}</p>
                {d.description && <p className="text-slate-600 mt-1">{d.description}</p>}
                <FamilyNote note={d.family_note} compact />
              </div>
              <ReadAloud text={text} testId={`suggestion-read-${d.id}`} />
            </div>
            <div className="flex flex-wrap gap-4 mt-5">
              <button data-testid={`suggestion-yes-${d.id}`} disabled={!!busy} onClick={() => decide(d, true)}
                className="flex-1 min-w-[180px] bg-[#16A34A] hover:bg-[#15803D] disabled:opacity-60 text-white text-2xl font-semibold min-h-[72px] rounded-2xl flex items-center justify-center gap-3"><Check className="w-8 h-8" /> Yes, book it</button>
              <button data-testid={`suggestion-no-${d.id}`} disabled={!!busy} onClick={() => decide(d, false)}
                className="min-w-[160px] bg-white border-2 border-[#E2D9CE] hover:border-slate-400 text-slate-700 text-xl font-semibold min-h-[72px] rounded-2xl flex items-center justify-center gap-2"><X className="w-6 h-6" /> No thanks</button>
            </div>
          </div>
        );
      })}
    </section>
  );
}
